import React, { useState, useRef, useEffect } from "react";

/**
 * CustomSelect - Dropdown personalizado compatible con navegadores antiguos
 *
 * Props:
 * - value: valor seleccionado
 * - options: array de { value, text }
 * - onChange: función callback (recibe el value)
 * - placeholder: texto cuando no hay selección
 * - disabled: deshabilitar el select
 * - hasError: mostrar estado de error
 * - name: nombre del campo (para formularios)
 */
const CustomSelect = ({
  value,
  options = [],
  onChange,
  placeholder = "Seleccione",
  disabled = false,
  hasError = false,
  name = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const optionsRef = useRef(null);
  // Búsqueda por teclado: acumula las letras escritas rápido (ej: "HON" -> HONDA)
  const typeaheadRef = useRef({ query: "", timer: null });

  // Encontrar el texto de la opción seleccionada
  const selectedOption = options.find(opt => String(opt.value) === String(value));
  const displayText = selectedOption ? selectedOption.text : placeholder;

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);


  // Limpiar el timer de la búsqueda por teclado al desmontar
  useEffect(() => {
    const typeahead = typeaheadRef.current;
    return () => {
      if (typeahead.timer) {
        clearTimeout(typeahead.timer);
      }
    };
  }, []);

  // Mantener visible la opción resaltada mientras se navega con el teclado
  useEffect(() => {
    if (!isOpen || highlightedIndex < 0 || !optionsRef.current) return;

    const list = optionsRef.current;
    const item = list.children[highlightedIndex];
    if (!item) return;

    if (item.offsetTop < list.scrollTop) {
      list.scrollTop = item.offsetTop;
    } else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
    }
  }, [highlightedIndex, isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      const opening = !isOpen;
      setIsOpen(opening);
      // Al abrir, resaltar la opción actualmente seleccionada
      setHighlightedIndex(
        opening ? options.findIndex(opt => String(opt.value) === String(value)) : -1
      );
    }
  };

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  // Busca la siguiente opción que empieza con el texto escrito.
  // startIndex indica desde dónde buscar; da la vuelta al llegar al final.
  const findMatch = (query, startIndex) => {
    for (let i = 0; i < options.length; i++) {
      const index = (startIndex + i) % options.length;
      const text = options[index].text;
      if (text && String(text).toLowerCase().indexOf(query) === 0) {
        return index;
      }
    }
    return -1;
  };

  const handleTypeahead = (char) => {
    const typeahead = typeaheadRef.current;

    if (typeahead.timer) {
      clearTimeout(typeahead.timer);
    }

    // Si se repite la misma letra, ciclar entre las opciones que empiezan con ella.
    // Si se escriben letras distintas, acumularlas para afinar la búsqueda.
    const repeatedChar = typeahead.query.length === 1 && typeahead.query === char;
    const query = repeatedChar ? char : typeahead.query + char;

    const baseIndex = highlightedIndex >= 0
      ? highlightedIndex
      : options.findIndex(opt => String(opt.value) === String(value));
    const startIndex = repeatedChar || typeahead.query === "" ? baseIndex + 1 : baseIndex;

    const matchIndex = findMatch(query, startIndex < 0 ? 0 : startIndex);

    typeahead.query = query;
    // Reiniciar la búsqueda si el usuario deja de escribir por un momento
    typeahead.timer = setTimeout(() => {
      typeahead.query = "";
    }, 700);

    if (matchIndex >= 0) {
      setIsOpen(true);
      setHighlightedIndex(matchIndex);
    }
  };

  const handleKeyDown = (event) => {
    if (disabled) return;

    const key = event.key;

    if (key === "Escape") {
      setIsOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    if (key === "ArrowDown" || key === "ArrowUp") {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        setHighlightedIndex(options.findIndex(opt => String(opt.value) === String(value)));
        return;
      }
      if (options.length === 0) return;

      const step = key === "ArrowDown" ? 1 : -1;
      setHighlightedIndex(prev => {
        const next = prev + step;
        if (next < 0) return options.length - 1;
        if (next >= options.length) return 0;
        return next;
      });
      return;
    }

    if (key === "Home" || key === "End") {
      if (!isOpen || options.length === 0) return;
      event.preventDefault();
      setHighlightedIndex(key === "Home" ? 0 : options.length - 1);
      return;
    }

    if (key === "Enter" || key === " ") {
      event.preventDefault();
      if (isOpen && highlightedIndex >= 0 && options[highlightedIndex]) {
        handleSelect(options[highlightedIndex].value);
      } else {
        handleToggle();
      }
      return;
    }

    if (key === "Tab") {
      setIsOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    // Búsqueda por letra/número (ignora combinaciones con Ctrl/Alt/Meta)
    if (key && key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) {
      event.preventDefault();
      handleTypeahead(key.toLowerCase());
    }
  };

  // Clases CSS
  const containerClass = [
    "custom-select-container",
    disabled ? "custom-select-disabled" : "",
    hasError ? "custom-select-error shake" : "",
    isOpen ? "custom-select-open" : ""
  ].filter(Boolean).join(" ");

  return (
    <div className={containerClass} ref={containerRef}>
      {/* Botón principal del select */}
      <div
        className="custom-select-trigger"
        onClick={handleToggle}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
      >
        <span className={selectedOption ? "custom-select-value" : "custom-select-placeholder"}>
          {displayText}
        </span>
        <span className="custom-select-arrow">
          <i className={isOpen ? "fas fa-chevron-up" : "fas fa-chevron-down"}></i>
        </span>
      </div>

      {/* Dropdown de opciones */}
      {isOpen && (
        <div className="custom-select-dropdown">
          {/* Lista de opciones */}
          <ul className="custom-select-options" ref={optionsRef}>
            {options.length > 0 ? (
              options.map((option, index) => {
                const optionClass = [
                  "custom-select-option",
                  String(option.value) === String(value) ? "selected" : "",
                  index === highlightedIndex ? "highlighted" : ""
                ].filter(Boolean).join(" ");

                return (
                  <li
                    key={index}
                    className={optionClass}
                    onClick={() => handleSelect(option.value)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {option.text}
                  </li>
                );
              })
            ) : (
              <li className="custom-select-no-results">Sin opciones</li>
            )}
          </ul>
        </div>
      )}

      {/* Input oculto para formularios */}
      <input type="hidden" name={name} value={value || ""} />
    </div>
  );
};

export default CustomSelect;
